import { ScopeOfWorkState, Area } from "./scope-of-work.types";
import { ScopeOfWorkAction } from "./scope-of-work.actions";
import { ScopeOfWorkInitialPayload } from "./scope-of-work.types";
import { normalizeAreasFromBackend } from "../helpers/normalizedAreasFromBackend";
import { SCOPE_OF_WORK_STEPS } from "./scope-of-work.constants";


export const createInitialScopeOfWorkState = (
  payload?: ScopeOfWorkInitialPayload
): ScopeOfWorkState => ({
  step: (payload?.step as any) ?? SCOPE_OF_WORK_STEPS[0],
  isComplete: false,
  squareft: payload?.squareft ?? null,
  averagePeople: payload?.averagePeople ?? null,
  category: payload?.category ?? null,
  places: payload?.places ?? null,
  service: payload?.service ?? null,
  areas: payload?.areas ?? [],
  otherAreas: payload?.otherAreas ?? [],
  objects: payload?.objects ?? [],
  specialRequests: payload?.specialRequests ?? [],
  costOverride: payload?.costOverride ?? null,
  serviceNameOverride: payload?.serviceNameOverride ?? "",
  travelBufferTimeInGeneral: payload?.travelBufferTimeInGeneral ?? 0,
});

const findArea = (
  state: ScopeOfWorkState,
  uid: string
): Area | undefined =>
  state.areas.find((a) => a.uid === uid) ??
  state.otherAreas.find((a) => a.uid === uid);

export function scopeOfWorkReducer(
  state: ScopeOfWorkState,
  action: ScopeOfWorkAction
): ScopeOfWorkState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.payload };

    case "SET_COST_OVERRIDE":
      return { ...state, costOverride: action.payload };

    case "SET_SERVICE_NAME_OVERRIDE":
      return { ...state, serviceNameOverride: action.payload };

    case "SET_FREQUENCY_TYPE":
      return { ...state, frequencyType: action.payload };

    case "NEXT_STEP": {
      const i = SCOPE_OF_WORK_STEPS.indexOf(state.step);
      if (i < SCOPE_OF_WORK_STEPS.length - 1) {
        return { ...state, step: SCOPE_OF_WORK_STEPS[i + 1] };
      }
      return { ...state, isComplete: true };
    }

    case "PREV_STEP": {
      const i = SCOPE_OF_WORK_STEPS.indexOf(state.step);
      if (i === 0) return state;
      return { ...state, step: SCOPE_OF_WORK_STEPS[i - 1] };
    }

    case "SET_CATEGORY":
      return { ...state, category: action.payload };

    case "SET_PLACE":
      return { ...state, places: action.payload };

    case "SET_SERVICE":
      return { ...state, service: action.payload };

    case "SET_SQUAREFT":
      return { ...state, squareft: action.payload };

    case "SET_AVERAGE_PEOPLE":
      return { ...state, averagePeople: action.payload };

    case "SET_AREAS":
      return { ...state, areas: action.payload };

    case "SET_OTHER_AREAS":
      return { ...state, otherAreas: action.payload };

    case "UPDATE_AREA_QUANTITY": {
      const area = findArea(state, action.payload.areaUid);
      if (!area) return state;

      const sq = area.squareFootages?.[0];
      if (!sq) return state;

      sq.quantity = Math.max(0, sq.quantity + action.payload.delta);
      if (sq.quantity === 0) {
        area.tasks.forEach((t) => (t.active = false));
      }
      return { ...state };
    }

    case "TOGGLE_TASK": {
      const area = findArea(state, action.payload.areaUid);
      if (!area) return state;
      const task = area.tasks.find(
        (t) => t.id === action.payload.taskUid || t.uid === action.payload.taskUid
      );
      if (task) task.active = !task.active;
      return { ...state };
    }

    case "UPDATE_TASK_NOTE": {
      const area = findArea(state, action.payload.areaUid);
      if (!area) return state;
      const task = area.tasks.find((t) => t.id === action.payload.taskId);
      if (task) task.note = action.payload.note;
      return { ...state };
    }

    case "SET_OBJECTS":
      return { ...state, objects: action.payload };

    case "TOGGLE_OBJECT": {
      const exists = state.objects.find((o) => o.uid === action.payload.uid);
      return exists
        ? { ...state, objects: state.objects.filter((o) => o.uid !== action.payload.uid) }
        : { ...state, objects: [...state.objects, action.payload] };
    }

    case "SET_SPECIAL_REQUESTS":
      return { ...state, specialRequests: action.payload };

    case "TOGGLE_SPECIAL_REQUEST": {
      const exists = state.specialRequests.find((r) => r.uid === action.payload.uid);
      return exists
        ? { ...state, specialRequests: state.specialRequests.filter((r) => r.uid !== action.payload.uid) }
        : { ...state, specialRequests: [...state.specialRequests, action.payload] };
    }

    case "RESET_WORKFLOW":
      return createInitialScopeOfWorkState();

    case "INITIALIZE_AREAS_FROM_BACKEND": {
      const { areas, squareFt } = action.payload;

      return {
        ...state,
        areas: normalizeAreasFromBackend(areas, squareFt),
        lastAreasSquareFtInitialized: squareFt,
      };
    }
    case "REMOVE_OTHER_AREA": {
      const { areaUid } = action.payload;

      return {
        ...state,
        otherAreas: (state.otherAreas ?? []).filter((a: any) => a.uid !== areaUid),
      };
    }

    case "SET_TASK_NOTE": {
      const { areaUid, taskUid, note } = action.payload;

      const updateList = (list: any[]) =>
        (list ?? []).map((a: any) => {
          if (a.uid !== areaUid) return a;

          const tasks = Array.isArray(a.tasks) ? a.tasks : [];
          const nextTasks = tasks.map((t: any) => {
            const tid = String(t.uid ?? t.id);
            if (tid !== String(taskUid)) return t;
            return { ...t, note };
          });

          return { ...a, tasks: nextTasks };
        });

      return {
        ...state,
        areas: updateList(state.areas),
        otherAreas: updateList(state.otherAreas),
      };
    }


    case "CHANGE_AREA_QUANTITY": {
      const { areaUid, delta } = action.payload;

      const updateList = (list: any[]) =>
        (list ?? [])?.map((a: any) => {
          if (a.uid !== areaUid) return a;

          const currentQty = Number(a.squareFootages?.[0]?.quantity ?? 0);
          const nextQty = Math.max(0, currentQty + Number(delta));

          // squareFootages[0].quantity
          const squareFootages = Array.isArray(a.squareFootages) ? [...a.squareFootages] : [];
          const first = squareFootages[0] ?? { squareFt: state.squareft, quantity: 0 };
          squareFootages[0] = { ...first, quantity: nextQty };

          // tasks: 0 => off, >0 => on
          const tasks = Array.isArray(a.tasks) ? a.tasks : [];
          const nextTasks = tasks?.map((t: any) => ({
            ...t,
            active: nextQty > 0,
          }));

          return { ...a, squareFootages, tasks: nextTasks };
        });

      return {
        ...state,
        areas: updateList(state.areas),
        otherAreas: updateList(state.otherAreas),
      };
    }



    case "TOGGLE_AREA_TASK_ACTIVE": {
      const { areaUid, taskUid } = action.payload;

      const toggleInList = (list: any[]) =>
        (list ?? [])?.map((a: any) => {
          if (a.uid !== areaUid) return a;

          const tasks = Array.isArray(a.tasks) ? a.tasks : [];
          return {
            ...a,
            tasks: tasks?.map((t: any) => {
              const tid = String(t.uid ?? t.id);
              if (tid !== String(taskUid)) return t;
              return { ...t, active: !t.active };
            }),
          };
        });

      return {
        ...state,
        areas: toggleInList(state.areas),
        otherAreas: toggleInList(state.otherAreas),
      };
    }






    default:
      return state;
  }
}
